import './style.modules.scss'

import { Dropdown, DropdownOption } from '@ampcore/adel-react'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import xss from 'xss'

import type { ShowOneProps } from './ShowOne.type'

export const ShowOne: React.FC<ShowOneProps> = ({
  options,
  panels,
  initialSelection = '',
  autoSelect = false,
  panelClassName,
  label,
  placeholder,
  buttonText = 'View',
  errorMessage,
  id = 'show-one-select',
  name = 'state',
  testId
}) => {
  const dropdownData = useMemo<DropdownOption[]>(
    () => options.map(option => ({ key: option.key, value: option.label })),
    [options]
  )

  const selectRef = useRef<HTMLSelectElement>(null)
  const [selected, setSelected] = useState<string>(initialSelection)
  const [pending, setPending] = useState<string>(initialSelection)

  // The ADEL Dropdown is uncontrolled and starts on the placeholder, so sync
  // the native select to a preselected value.
  useEffect(() => {
    if (initialSelection && selectRef.current) {
      selectRef.current.value = initialSelection
    }
  }, [initialSelection])

  const hasMatch = panels.some(panel => panel.name === selected)
  const showError = Boolean(selected) && !hasMatch

  const onDropdownChange = (key: string) => {
    if (autoSelect) {
      setSelected(key)
    } else {
      setPending(key)
    }
  }

  return (
    <div
      className="ShowOne"
      data-testid={testId ? `${testId}-container` : undefined}
    >
      <div className="ShowOne-controls u-flex u-flexWrap u-flexJustifyStart u-lg-flexJustifyCenter u-flexAlignItemsBaseline u-marginVert32 u-flexCol u-lg-flexRow">
        {label && (
          <div className="ShowOne-label u-paddingHoriz32 u-marginBottom8">
            <label htmlFor={id} className="Type-4">
              {label}
            </label>
          </div>
        )}
        <div className="ShowOne-field u-flex u-flexRow u-flexAlignItemsBaseline u-paddingHoriz32">
          <Dropdown
            id={id}
            name={name}
            placeholder={placeholder}
            data={dropdownData}
            reference={selectRef}
            callback={onDropdownChange}
            testId={testId ? `${testId}-dropdown` : undefined}
          />
          {!autoSelect && (
            <button
              type="button"
              className="ShowOne-button view-selected Button Button--primary u-marginLeft16"
              onClick={() => setSelected(pending)}
              data-testid={testId ? `${testId}-button` : undefined}
            >
              {buttonText}
            </button>
          )}
        </div>
      </div>

      {panels.map(panel => (
        <div
          key={panel.name}
          className={panelClassName}
          hidden={panel.name !== selected}
          data-show-if={panel.name}
          data-testid={testId ? `${testId}-panel-${panel.name}` : undefined}
          dangerouslySetInnerHTML={{ __html: xss(panel.content) }}
        />
      ))}

      {errorMessage && (
        <div
          className="ShowOne-error error-panel"
          hidden={!showError}
          data-testid={testId ? `${testId}-error` : undefined}
        >
          {errorMessage}
        </div>
      )}
    </div>
  )
}
